import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import { IconButton, Stack, TextField, Button, Grow, Typography, Icons } from "@curves/ui-base";
import { ProductInfo } from '@curves/ui-shared';
import { useSkuAddToListContext } from "./SkuAddToListContext";

/** 自動關閉延遲時間（秒） */
const AUTO_CLOSE_DELAY = 3;

/**
 * 商品加入清單後的數量確認彈窗
 * 商品已直接加入清單，此彈窗供使用者確認或修改數量，不輸入也可直接關閉繼續操作
 */
const SkuAddConfirmDialog = (props) => {
	const { quantityEditTarget, handleCloseQuantityEdit, handleConfirmQuantity } = useSkuAddToListContext();

	const { productKey, product, source, openId } = quantityEditTarget || {};
	const isFromAdd = source !== "edit"; // 加入車：有倒數（預設或 source === "add"）
	const isFromEdit = source === "edit"; // 點擊清單：純編輯，無倒數

	const [editQuantity, setEditQuantity] = useState(1);
	const [countdown, setCountdown] = useState(AUTO_CLOSE_DELAY);
	const [isEditMode, setIsEditMode] = useState(false);

	// 僅在「本輪」倒數到 0 時才關閉，避免新 add 時沿用上一輪的 countdown=0 誤觸關閉
	const countdownReachedZeroRef = useRef(false);

	// 倒數模式：倒數中為 true，點擊進入編輯後為 false
	const isCountdownMode = isFromAdd && !isEditMode;

	const handleQuantityDelta = (delta) => {
		const newQuantity = editQuantity + delta;
		if (newQuantity >= 0) setEditQuantity(newQuantity);
		setIsEditMode(true);
	};

	// 確認修改數量，關閉視窗
	const handleConfirm = () => {
		if (productKey != null) {
			handleConfirmQuantity(productKey, editQuantity);
		}
	};

	// 點擊視窗進入編輯模式，停止倒數（僅在加入車情境有效）
	const handleDialogClick = () => {
		if (isFromAdd && !isEditMode) setIsEditMode(true);
	};

	// 每次 add 開彈窗都重設計時器：從 AUTO_CLOSE_DELAY 秒倒數，並清除「本輪到 0」標記
	useEffect(() => {
		if (quantityEditTarget) {
			const qty = product?.quantity;
			setEditQuantity(qty != null ? Number(qty) : 1);
			setCountdown(AUTO_CLOSE_DELAY);
			countdownReachedZeroRef.current = false;
			setIsEditMode(isFromEdit); // 點擊清單開啟時直接為編輯模式
		}
	},
		// eslint-disable-next-line
		[quantityEditTarget, product?.quantity, isFromEdit]);

	// 倒數計時：僅在「本輪」倒數到 0 時關閉視窗（handleConfirmQuantity），避免新 add 時沿用上一輪的 0 誤關
	useEffect(() => {
		let timeoutId;
		if (quantityEditTarget && isCountdownMode) {
			if (countdown <= 0 && countdownReachedZeroRef.current) {
				handleConfirmQuantity(productKey, editQuantity);
				return;
			}
			timeoutId = setTimeout(() => {
				setCountdown((prev) => {
					if (prev <= 1) {
						countdownReachedZeroRef.current = true;
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}
		return () => clearTimeout(timeoutId);
	},
		// eslint-disable-next-line
		[quantityEditTarget, isCountdownMode, countdown, handleConfirmQuantity, productKey, editQuantity]);

	// 沒有 target 就不渲染 Dialog
	if (!quantityEditTarget) return null;

	return (
		<Dialog
			key={openId ?? productKey}
			open={!!quantityEditTarget}
			onClose={handleCloseQuantityEdit}
			onClick={handleDialogClick}
			PaperProps={{ className: "sku-add-confirm-dialog", style: { minWidth: "500px" } }}
			TransitionComponent={Grow}
			TransitionProps={{ timeout: 300 }}
			{...props}
		>
			<DialogContent className="p-4">
				<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3} className="py-2">
					<ProductInfo
						product={{
							name: product?.productName,
							sku: product?.sku,
							imageSrc: product?.pictureSrc,
							spec: product?.variantName,
						}}
					/>

					<Stack direction="row" alignItems="center" className="quantity-edit-group">
						<Button size="small" onClick={(e) => { e.stopPropagation(); handleQuantityDelta(-1); }} disabled={editQuantity <= 0}>
							<Icons.Minus className="text-primary" />
						</Button>
						<TextField
							value={editQuantity}
							onChange={(e) => {
								const val = Number(e.target.value);
								if (!Number.isNaN(val) && val >= 0) {
									setEditQuantity(val);
									setIsEditMode(true);
								}
							}}
							inputProps={{ min: 0, style: { textAlign: "center", width: "40px" } }}
						/>
						<Button size="small" onClick={(e) => { e.stopPropagation(); handleQuantityDelta(1); }}>
							<Icons.Plus className="text-primary" />
						</Button>
					</Stack>
				</Stack>

				{isCountdownMode && (
					<Typography variant="body2" className="text-center mt-4 font-color-3">
						{countdown > 0 ? `${countdown} 秒後自動關閉...點擊可編輯數量` : "即將自動關閉..."}
					</Typography>
				)}

				{/* 倒數時不顯示確認按鈕；進入編輯模式或從清單點擊開啟時顯示 */}
				{!isCountdownMode && (
					<Stack direction="row" spacing={2} className="mt-4" justifyContent="end">
						<Button variant="contained" onClick={(e) => { e.stopPropagation(); handleConfirm(); }}>
							確認
						</Button>
					</Stack>
				)}

				<IconButton
					onClick={(e) => { e.stopPropagation(); handleCloseQuantityEdit(); }}
					style={{
						position: "absolute",
						top: "8px",
						right: "8px",
						padding: "4px",
					}}
				>
					<Icons.ErrorCircle style={{ fontSize: "32px", color: "#aaa" }} />
				</IconButton>
			</DialogContent>
		</Dialog>
	);
};

export default SkuAddConfirmDialog;
